import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Field, Section, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError, newIdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import type { MigrationPreview, MigrationResult, Store } from '../lib/types';
import { FilePicker } from './imports';

interface Options {
  targetStoreId: string;
  includeStock: boolean;
  overwrite: boolean;
  legalReviewed: boolean;
  carryExpertApprovals: boolean;
}

const optionBody = (o: Options) => ({
  ...(o.targetStoreId ? { targetStoreId: o.targetStoreId } : {}),
  includeStock: o.includeStock,
  overwrite: o.overwrite,
  legalReviewed: o.legalReviewed,
  carryExpertApprovals: o.carryExpertApprovals,
});

function CheckRow({
  checked,
  onChange,
  label,
  help,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  help: Parameters<typeof HelpTip>[0]['k'];
}) {
  return (
    <div className="flex items-start gap-1">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1 accent-[var(--zt-pine)]"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{label}</span>
      </label>
      <HelpTip k={help} />
    </div>
  );
}

/**
 * Staging → canlı taşıma paketi (F3B-15, ADR-041). Dışa aktar: mağazanın yapılandırma ve içeriği (ZIP).
 * İçe aktar: önizleme (ne eklenecek/güncellenecek, stok satırları, onay gerektirenler) → onaylı uygulama.
 * Yalnızca işletme sahibi; iki adımlı doğrulama ister.
 */
export function MigrationPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  const [exportStore, setExportStore] = useState('');
  const [exporting, setExporting] = useState(false);
  const [opts, setOpts] = useState<Options>({
    targetStoreId: '',
    includeStock: true,
    overwrite: false,
    legalReviewed: false,
    carryExpertApprovals: false,
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<MigrationPreview | null>(null);
  const [stale, setStale] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const storeList = (stores.data ?? []).filter((s) => s.status !== 'archived');
  const sourceStore = exportStore || storeList[0]?.id || '';

  const runExport = async () => {
    setExporting(true);
    try {
      const { blob, fileName } = await api.blob(`/v1/admin/migration/export?storeId=${sourceStore}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'tasima-paketi.zip';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('admin.migration.exported'));
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'step_up_required'))
        toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };
  const upload = async (file: File) => {
    setBusy(true);
    setResult(null);
    try {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(optionBody(opts))) qs.set(k, String(v));
      const fd = new FormData();
      fd.append('file', file);
      setPreview(await api.upload<MigrationPreview>(`/v1/admin/migration/preview?${qs.toString()}`, fd));
      setStale(false);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  const replan = useApiMutation(
    () => api.post<MigrationPreview>(`/v1/admin/migration/${preview!.jobId}/preview`, optionBody(opts)),
    {
      onSuccess: (r) => {
        setPreview(r);
        setStale(false);
      },
    },
  );
  const apply = useApiMutation(
    () =>
      api.post<MigrationResult>(`/v1/admin/migration/${preview!.jobId}/apply`, optionBody(opts), {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      invalidate: [['stores'], ['products'], ['categories'], ['setup']],
      success: t('admin.migration.applied'),
      onSuccess: (r) => {
        setResult(r);
        setPreview(null);
      },
    },
  );
  const set = (patch: Partial<Options>) => {
    setOpts({ ...opts, ...patch });
    if (preview) setStale(true);
  };
  const shown = result ?? preview;

  return (
    <PageFrame helpKey="page.migration">
      <Section title={t('admin.migration.exportTitle')} description={t('admin.migration.exportLead')}>
        {!stores.data ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('admin.migration.sourceStore')} className="min-w-64">
              {(p) => (
                <Select {...p} value={sourceStore} onChange={(e) => setExportStore(e.target.value)}>
                  {storeList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <span className="flex items-center gap-1">
              <Button loading={exporting} disabled={!sourceStore} onClick={() => void runExport()}>
                <Download className="h-4 w-4" aria-hidden />
                {t('admin.migration.export')}
              </Button>
              <HelpTip k="migration.export" />
            </span>
          </div>
        )}
        <ul className="mt-4 list-disc space-y-1 ps-5 text-sm text-ink-2">
          <li>{t('admin.migration.included')}</li>
          <li>{t('admin.migration.excluded')}</li>
        </ul>
      </Section>

      <Section title={t('admin.migration.importTitle')} description={t('admin.migration.importLead')}>
        <div className="space-y-3">
          <Field
            label={t('admin.migration.targetStore')}
            help={<HelpTip k="migration.target_store" />}
            className="max-w-md"
          >
            {(p) => (
              <Select
                {...p}
                value={opts.targetStoreId}
                onChange={(e) => set({ targetStoreId: e.target.value })}
              >
                <option value="">{t('admin.migration.targetFromPackage')}</option>
                {storeList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <CheckRow
            checked={opts.includeStock}
            onChange={(v) => set({ includeStock: v })}
            label={t('admin.migration.includeStock')}
            help="migration.include_stock"
          />
          <CheckRow
            checked={opts.overwrite}
            onChange={(v) => set({ overwrite: v })}
            label={t('admin.migration.overwrite')}
            help="migration.overwrite"
          />
          <CheckRow
            checked={opts.legalReviewed}
            onChange={(v) => set({ legalReviewed: v })}
            label={t('admin.migration.legalReviewed')}
            help="migration.legal_reviewed"
          />
          <CheckRow
            checked={opts.carryExpertApprovals}
            onChange={(v) => set({ carryExpertApprovals: v })}
            label={t('admin.migration.carryApprovals')}
            help="migration.carry_approvals"
          />
          <span className="flex items-center gap-1">
            <FilePicker
              busy={busy}
              onFile={(f) => void upload(f)}
              label={t('admin.migration.chooseFile')}
              accept=".zip,application/zip"
            />
            <HelpTip k="migration.file" />
          </span>
        </div>

        {shown && (
          <div
            className="mt-5 space-y-4 rounded-md border border-line p-4"
            role={result ? 'status' : undefined}
          >
            {result ? (
              <p className="font-medium">
                {t('admin.migration.resultTitle', { store: result.target.storeName })}
              </p>
            ) : (
              preview && (
                <p className="text-sm text-ink-2">
                  {t('admin.migration.previewOf', {
                    org: preview.source.organization,
                    store: preview.source.store.name,
                    env: preview.source.environment ?? '—',
                    date: formatDate(preview.exportedAt),
                  })}
                </p>
              )
            )}
            {shown.target.create && !result && (
              <p className="text-sm">
                {t('admin.migration.storeWillCreate', { store: shown.target.storeName })}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[30rem] text-sm [&_td]:px-2 [&_th]:px-2">
                <caption className="sr-only">{t('admin.migration.sectionsCaption')}</caption>
                <thead className="text-ink-2">
                  <tr>
                    <th className="py-1 text-start font-medium">{t('admin.migration.section')}</th>
                    <th className="py-1 text-end font-medium">{t('admin.migration.create')}</th>
                    <th className="py-1 text-end font-medium">{t('admin.migration.update')}</th>
                    <th className="py-1 text-end font-medium">{t('admin.migration.unchanged')}</th>
                    <th className="py-1 text-end font-medium">{t('admin.migration.skipped')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.sections
                    .filter(
                      (x) => x.counts.create + x.counts.update + x.counts.unchanged + x.counts.skipped > 0,
                    )
                    .map((x) => (
                      <tr key={x.key} className="border-t border-line">
                        <td className="py-1">{t(`admin.migration.sections.${x.key}`)}</td>
                        <td className="py-1 text-end tabular">{x.counts.create}</td>
                        <td className="py-1 text-end tabular">{x.counts.update}</td>
                        <td className="py-1 text-end tabular">{x.counts.unchanged}</td>
                        <td className={`py-1 text-end tabular ${x.counts.skipped ? 'text-amber' : ''}`}>
                          {x.counts.skipped}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {shown.stock.length > 0 && (
              <div className="overflow-x-auto">
                <p className="mb-1 font-medium">
                  {opts.includeStock ? t('admin.migration.stockTitle') : t('admin.migration.stockSkipped')}
                </p>
                <table className="w-full min-w-[36rem] text-sm [&_td]:px-2 [&_th]:px-2">
                  <caption className="sr-only">{t('admin.migration.stockTitle')}</caption>
                  <thead className="text-ink-2">
                    <tr>
                      <th className="py-1 text-start font-medium">{t('admin.migration.product')}</th>
                      <th className="py-1 text-start font-medium">SKU</th>
                      <th className="py-1 text-start font-medium">{t('admin.stock.warehouse')}</th>
                      <th className="py-1 text-start font-medium">{t('admin.stock.lot')}</th>
                      <th className="py-1 text-start font-medium">{t('admin.stock.expiry')}</th>
                      <th className="py-1 text-end font-medium">{t('admin.stock.quantity')}</th>
                      <th className="py-1 text-start font-medium">{t('admin.migration.stockStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.stock.map((l, i) => (
                      <tr key={i} className="border-t border-line">
                        <td className="py-1">{l.title}</td>
                        <td className="py-1 tabular">{l.sku}</td>
                        <td className="py-1">{l.warehouseName}</td>
                        <td className="py-1">{l.lotNo ?? '—'}</td>
                        <td className="py-1">{l.expiryDate ?? '—'}</td>
                        <td className="py-1 text-end tabular">{l.quantity}</td>
                        <td className="py-1">{t(`admin.migration.stockStatus_${l.status}`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {Object.values(shown.approvals).some((n) => n > 0) && (
              <p className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm text-ink">
                {t('admin.migration.approvalsPending', {
                  products: shown.approvals.product,
                  content: shown.approvals.page + shown.approvals.post + shown.approvals.banner,
                })}
              </p>
            )}
            {shown.legal.length > 0 && (
              <p className="text-sm text-ink-2">
                {t(
                  opts.legalReviewed
                    ? 'admin.migration.legalWithReview'
                    : 'admin.migration.legalWithoutReview',
                  {
                    list: shown.legal.map((l) => l.title).join(', '),
                  },
                )}
              </p>
            )}
            <ul className="list-disc space-y-1 ps-5 text-sm text-ink-2">
              {shown.warnings.map((w) => (
                <li key={w}>{t(`admin.migration.warnings.${w}`)}</li>
              ))}
            </ul>
            {preview && !result && (
              <div className="flex flex-wrap items-center gap-2">
                {stale && (
                  <span className="flex items-center gap-1">
                    <Button loading={replan.isPending} onClick={() => replan.mutate(undefined)}>
                      {t('admin.migration.refresh')}
                    </Button>
                    <HelpTip k="migration.refresh" />
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <ConfirmButton
                    variant="primary"
                    message={t('admin.migration.applyConfirm')}
                    onConfirm={() => apply.mutateAsync(undefined)}
                  >
                    {t('admin.migration.apply')}
                  </ConfirmButton>
                  <HelpTip k="migration.apply" />
                </span>
                {stale && <Badge tone="amber">{t('admin.migration.stale')}</Badge>}
              </div>
            )}
          </div>
        )}
      </Section>
    </PageFrame>
  );
}
